"use client";

import { ClipboardList } from "lucide-react";
import { useState } from "react";
import EditableCard from "@/src/components/cards/editable-card";
import ICD10DiagnosisForm from "@/src/components/cards/icd10-diagnosis-form";
import FormField from "@/src/components/form-field";
import ServiceLineSelector from "@/src/components/tasks/service-line-selector";
import { toast } from "@/src/hooks/use-toast";
import { useReferralUpdateMutation } from "@/src/lib/data-fetchers/referrals-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { convertEmptyStringValuesToNull, enumToOptions } from "@/src/lib/utils/object-utils";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Referral, SchedulingPriority, ReferralErrors, ReferralFieldKey, validateReferral } from "@/src/models/documents/referral";

// List of fields to validate for this card
const fieldsToValidate: ReferralFieldKey[] = ["dateReceived", "dateOfReferral", "reasonForReferral", "schedulingPriority", "serviceLine", "diagnosisList", "authorizationNumber"];
interface ReferralCardEditableProps {
  referral: Referral;
  onClose: () => void;
}
export default function ReferralCardEditable({
  referral,
  onClose
}: ReferralCardEditableProps) {
  const [formData, setFormData] = useState<Referral>({
    ...referral
  });
  const [errors, setErrors] = useState<ReferralErrors>({});
  const updateReferralMutation = useReferralUpdateMutation();
  const handleInputChange = (field: ReferralFieldKey, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev_0 => {
        const {
          [field]: _,
          ...rest
        } = prev_0;
        return rest;
      });
    }
  };
  const handleDiagnosisListChange = (items: {
    diagnosisCode: string | null;
    diagnosisText: string | null;
    diagnosisCodeType: string | null;
  }[]) => {
    const updatedItems = items.map(item => ({
      ...item,
      diagnosisCodeType: item.diagnosisCodeType ?? null
    }));
    setFormData(prev_1 => ({
      ...prev_1,
      diagnosisList: updatedItems
    }));
  };
  const handleSave = async () => {
    const processedFormData = convertEmptyStringValuesToNull(formData);
    const {
      isValid,
      errors: errors_0
    } = validateReferral(processedFormData, fieldsToValidate);
    setErrors(errors_0);
    if (!isValid) {
      return;
    }
    try {
      await updateReferralMutation.mutateAsync(processedFormData);
      toast({
        title: "Referral updated",
        description: "The referral information was successfully saved.",
        variant: "default"
      });
      onClose?.();
    } catch (error) {
      logger.error("Error saving referral:", {
        error
      });
    }
  };
  return <EditableCard title="Edit Referral Information" icon={ClipboardList} initialData={referral} onCancel={onClose} onSave={handleSave} isSaving={updateReferralMutation.isPending}>
      <div className="space-y-6">
        <div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Date Received" value={formData.dateReceived} onChange={value_0 => handleInputChange("dateReceived", value_0)} type="date" error={errors.dateReceived} />
            <FormField label="Date of Referral" value={formData.dateOfReferral} onChange={value_1 => handleInputChange("dateOfReferral", value_1)} type="date" error={errors.dateOfReferral} />
            <FormField label="Scheduling Priority" value={formData.schedulingPriority} onChange={value_2 => handleInputChange("schedulingPriority", value_2)} type="select" options={enumToOptions(SchedulingPriority)} error={errors.schedulingPriority} />
          </div>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <FormField label="Reason for Referral" value={formData.reasonForReferral} onChange={value_3 => handleInputChange("reasonForReferral", value_3)} type="textarea" required rows={8} error={errors.reasonForReferral} />
          </div>
        </div>

        {/* Authorization Number */}
        <div className="grid grid-cols-1 gap-4 mt-4">
          <FormField label="Authorization Number" value={formData.authorizationNumber} onChange={value_4 => handleInputChange("authorizationNumber", value_4)} type="text" error={errors.authorizationNumber} />
        </div>

        {/* Service Line */}
        <div>
          <ServiceLineSelector value={formData.serviceLine} onChange={(value_5: string) => {
          setFormData(prev_2 => ({
            ...prev_2,
            serviceLine: value_5 as ServiceLine
          }));
        }} isRequired={false} fieldKey="serviceLine" />
        </div>

        {/* Diagnosis Information */}
        <div>
          <ICD10DiagnosisForm items={formData.diagnosisList || []} onChange={handleDiagnosisListChange} label="Diagnosis List" isRequired={false} fieldKey="diagnosisList" />
        </div>
      </div>
    </EditableCard>;
}